import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  User, Compass, Map, Trophy, Star, Target, Calendar,
  GraduationCap, BarChart3, BookOpen, Briefcase, Award,
  ChevronRight, Menu, X, LogOut, Home, Zap, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── 6 Tile Definitions ────────────────────────────────────────────────────────
const TILES = [
  {
    id: 'me',
    label: 'ME',
    subtitle: 'Who I am',
    description: 'Assessment, strengths, career interests, and my profile.',
    icon: User,
    color: 'cyan',
    gradient: 'from-cyan-500/30 to-cyan-500/5',
    border: 'border-cyan-500/40',
    hover: 'hover:border-cyan-400/80 hover:from-cyan-500/40',
    text: 'text-cyan-400',
    links: [
      { label: 'Strengths & Interests', path: '/app/assessment' },
      { label: 'My Profile', path: '/app/profile' },
      { label: 'Skills Profile', path: '/app/skills' },
    ],
  },
  {
    id: 'my-future',
    label: 'MY FUTURE',
    subtitle: 'Where I\'m headed',
    description: 'Career explorer, future self, and how the world of work is changing.',
    icon: Compass,
    color: 'blue',
    gradient: 'from-blue-500/30 to-blue-500/5',
    border: 'border-blue-500/40',
    hover: 'hover:border-blue-400/80 hover:from-blue-500/40',
    text: 'text-blue-400',
    links: [
      { label: 'Career Explorer', path: '/app/careers' },
      { label: 'Education Options', path: '/app/pathways' },
    ],
  },
  {
    id: 'my-path',
    label: 'MY PATH',
    subtitle: 'My plan to get there',
    description: 'Career Development Plan, 4-year course planner, and CTE pathways.',
    icon: Map,
    color: 'violet',
    gradient: 'from-violet-500/30 to-violet-500/5',
    border: 'border-violet-500/40',
    hover: 'hover:border-violet-400/80 hover:from-violet-500/40',
    text: 'text-violet-400',
    links: [
      { label: 'Career Development Plan', path: '/app/nc-cdp' },
      { label: '4-Year Course Plan', path: '/app/course-planner' },
      { label: 'CTE Pathways', path: '/app/nc-cte' },
    ],
  },
  {
    id: 'my-readiness',
    label: 'MY READINESS',
    subtitle: 'Am I on track?',
    description: 'Graduation predictor, credits on track, and career launch readiness score.',
    icon: BarChart3,
    color: 'emerald',
    gradient: 'from-emerald-500/30 to-emerald-500/5',
    border: 'border-emerald-500/40',
    hover: 'hover:border-emerald-400/80 hover:from-emerald-500/40',
    text: 'text-emerald-400',
    links: [
      { label: 'Graduation Readiness', path: '/app/graduation-readiness' },
      { label: 'Career Readiness Score', path: '/app/readiness' },
      { label: 'My FuturePath Report', path: '/app/report' },
    ],
  },
  {
    id: 'my-opportunities',
    label: 'MY OPPORTUNITIES',
    subtitle: 'Skills & credentials',
    description: 'Credential tracker, work-based learning hours, and certificate programs.',
    icon: Trophy,
    color: 'amber',
    gradient: 'from-amber-500/30 to-amber-500/5',
    border: 'border-amber-500/40',
    hover: 'hover:border-amber-400/80 hover:from-amber-500/40',
    text: 'text-amber-400',
    links: [
      { label: 'Credential Tracker', path: '/app/credentials' },
      { label: 'Work-Based Learning', path: '/app/nc-wbl' },
      { label: 'Certificate Programs', path: '/app/certificates' },
    ],
  },
  {
    id: 'my-plan',
    label: 'MY PLAN',
    subtitle: 'Actions & events',
    description: 'Upcoming events, goals, milestones, and recommendation letters.',
    icon: Calendar,
    color: 'pink',
    gradient: 'from-pink-500/30 to-pink-500/5',
    border: 'border-pink-500/40',
    hover: 'hover:border-pink-400/80 hover:from-pink-500/40',
    text: 'text-pink-400',
    links: [
      { label: 'Events & Opportunities', path: '/app/events' },
      { label: 'Goals & Milestones', path: '/app/goals' },
      { label: 'Recommendation Letters', path: '/app/rec-letters' },
    ],
  },
];

// ── Tile Card ──────────────────────────────────────────────────────────────────
function TileCard({ tile, stats }) {
  const [expanded, setExpanded] = useState(false);
  const Icon = tile.icon;

  return (
    <div
      className={`relative p-5 rounded-2xl border-2 bg-gradient-to-br ${tile.gradient} ${tile.border} ${tile.hover} transition-all duration-200 cursor-pointer group`}
      onClick={() => setExpanded(!expanded)}
      data-testid={`tile-${tile.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-3 rounded-xl bg-slate-900/50 border ${tile.border}`}>
          <Icon className={`w-6 h-6 ${tile.text}`} />
        </div>
        {stats?.[tile.id] && (
          <div className={`px-2.5 py-1 rounded-full text-xs font-bold ${tile.text} bg-slate-900/50`}>
            {stats[tile.id]}
          </div>
        )}
      </div>

      <h3 className={`font-outfit font-bold text-lg mb-0.5 ${tile.text}`}>{tile.label}</h3>
      <p className="text-slate-300 text-sm font-medium mb-1">{tile.subtitle}</p>
      <p className="text-slate-300 text-xs leading-relaxed mb-3">{tile.description}</p>

      {/* Quick links */}
      <div
        className={`space-y-1.5 overflow-hidden transition-all duration-300 ${expanded ? 'max-h-40 opacity-100' : 'max-h-0 opacity-0'}`}
        onClick={e => e.stopPropagation()}
      >
        {tile.links.map(link => (
          <Link
            key={link.path}
            to={link.path}
            data-testid={`tile-link-${link.path.replace('/app/', '')}`}
            className={`flex items-center justify-between px-3 py-2 rounded-lg bg-slate-900/50 border ${tile.border} text-slate-300 hover:text-white text-xs transition-colors group/link`}
          >
            <span>{link.label}</span>
            <ChevronRight className={`w-3.5 h-3.5 ${tile.text} group-hover/link:translate-x-0.5 transition-transform`} />
          </Link>
        ))}
      </div>

      {/* Expand hint */}
      <div className={`flex items-center gap-1 mt-2 ${tile.text} opacity-60 text-xs`}>
        {expanded ? (
          <><X className="w-3 h-3" /> Hide links</>
        ) : (
          <><ChevronRight className="w-3 h-3" /> Quick links</>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────
export default function InstitutionalStudentDashboard() {
  const { user, logout, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tileStats, setTileStats] = useState({});

  // Fetch quick stats for the tiles
  useEffect(() => {
    if (!user) return;
    const headers = getAuthHeader ? getAuthHeader() : {};

    const stats = {};

    // Graduation readiness
    axios.get(`${API}/graduation/prediction`, headers)
      .then(r => {
        if (r.data?.readiness_score !== undefined) {
          stats['my-readiness'] = `${r.data.readiness_score}%`;
          setTileStats(s => ({ ...s, ...stats }));
        }
      }).catch(() => {});

    // Credentials
    axios.get(`${API}/credentials`, headers)
      .then(r => {
        const count = (r.data?.credentials || []).length;
        if (count > 0) {
          setTileStats(s => ({ ...s, 'my-opportunities': `${count} credentials` }));
        }
      }).catch(() => {});

    // CDP progress
    axios.get(`${API}/nc/cdp`, headers)
      .then(r => {
        const pct = r.data?.overall_completion;
        if (pct !== undefined) {
          setTileStats(s => ({ ...s, 'my-path': `${pct}% CDP` }));
        }
      }).catch(() => {});
  }, [user, getAuthHeader]);

  const handleLogout = () => {
    localStorage.removeItem('institutional_shell');
    logout();
    navigate('/');
  };

  const exitInstitutionalShell = () => {
    localStorage.removeItem('institutional_shell');
    navigate('/app/dashboard');
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#0a1729] flex items-center justify-center">
        <div className="text-white">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1729] text-white">
      {/* Top navigation bar */}
      <header className="sticky top-0 z-20 bg-slate-900/90 backdrop-blur-xl border-b border-slate-800/60">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/fp-logo.png" alt="FuturePath AI" className="h-7 w-7 rounded-md flex-shrink-0" />
            <div>
              <p className="font-outfit font-bold text-white text-sm leading-tight">
                <span className="text-cyan-300">FuturePath</span> AI
              </p>
              <p className="text-slate-300 text-xs leading-tight hidden sm:block">Student Portal</p>
            </div>
          </div>

          {/* User info */}
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/40">
              <div className="w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center">
                <User className="w-3.5 h-3.5 text-cyan-400" />
              </div>
              <span className="text-sm text-slate-200">{user.full_name || user.email}</span>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-800 transition-colors"
              data-testid="institutional-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown menu */}
        {mobileMenuOpen && (
          <div className="border-t border-slate-800/60 bg-slate-900/95 px-4 py-3 space-y-1">
            <button
              onClick={exitInstitutionalShell}
              className="flex items-center gap-2 w-full px-3 py-2 text-slate-300 hover:text-cyan-400 hover:bg-slate-800 rounded-lg text-sm transition-colors"
            >
              <Home className="w-4 h-4" /> Full FuturePath View
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 w-full px-3 py-2 text-slate-300 hover:text-red-400 hover:bg-red-500/10 rounded-lg text-sm transition-colors"
            >
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Welcome header */}
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-slate-300 text-sm mb-1">Welcome back</p>
              <h1 className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-2">
                {user.full_name?.split(' ')[0] || 'Student'}
              </h1>
              <p className="text-slate-300 text-base">Your FuturePath, in 6 easy sections.</p>
            </div>
            <button
              onClick={exitInstitutionalShell}
              data-testid="exit-institutional-shell-btn"
              className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-700/60 text-slate-300 hover:text-white hover:border-slate-500 text-sm transition-colors flex-shrink-0"
            >
              <Zap className="w-4 h-4" /> Full View
            </button>
          </div>
        </div>

        {/* 6 tiles grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {TILES.map(tile => (
            <TileCard key={tile.id} tile={tile} stats={tileStats} />
          ))}
        </div>

        {/* Quick actions bar */}
        <div className="mt-8 p-4 rounded-2xl bg-slate-800/30 border border-slate-700/40">
          <p className="text-slate-300 text-xs uppercase tracking-wide font-semibold mb-3">Quick Actions</p>
          <div className="flex flex-wrap gap-2">
            {[
              { label: 'View CDP', path: '/app/nc-cdp', icon: FileText },
              { label: 'Log WBL Hours', path: '/app/nc-wbl', icon: Briefcase },
              { label: 'Add Credential', path: '/app/credentials', icon: Award },
              { label: 'Check Graduation', path: '/app/graduation-readiness', icon: GraduationCap },
              { label: 'Upcoming Events', path: '/app/events', icon: Calendar },
            ].map(action => {
              const ActionIcon = action.icon;
              return (
                <Link
                  key={action.path}
                  to={action.path}
                  data-testid={`quick-action-${action.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/40 text-slate-300 hover:text-white hover:border-slate-600 text-xs transition-colors"
                >
                  <ActionIcon className="w-3.5 h-3.5" />
                  {action.label}
                </Link>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
